import { useEffect, useState, useRef } from "react";
import { initializePaddle } from "@paddle/paddle-js";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { X, CreditCard, ShieldCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { track } from "@/lib/analytics";

const PADDLE_TOKEN = process.env.REACT_APP_PADDLE_CLIENT_TOKEN;
const PADDLE_ENV = process.env.REACT_APP_PADDLE_ENV || "production";
const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;

/**
 * Presents Paddle (primary) and PayPal payment options for a chosen package.
 * Props: open, onClose, packageId ("single_report" | "credit_bundle_5"),
 *        projectId, amountLabel (e.g. "$149"), packageName, onPaid(txn) callback.
 */
export const PaymentMethodModal = ({ open, onClose, packageId, projectId, amountLabel, packageName, onPaid }) => {
  const [method, setMethod] = useState("paddle"); // "paddle" | "paypal"
  const [paddle, setPaddle] = useState(null);
  const [initing, setIniting] = useState(false);
  const [busy, setBusy] = useState(false);
  const currentTxnRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    if (!PADDLE_TOKEN) return;
    if (paddle) return;
    setIniting(true);
    initializePaddle({
      token: PADDLE_TOKEN,
      environment: PADDLE_ENV === "sandbox" ? "sandbox" : "production",
      eventCallback: (data) => {
        if (data?.name === "checkout.completed") {
          const txnId = data?.data?.transaction_id || currentTxnRef.current;
          confirmPaymentStatus(txnId).then((paid) => {
            if (paid) {
              track("report_purchase", { method: "paddle", package_id: packageId });
              toast.success("Payment received — unlocking report");
              onPaid?.({ provider: "paddle", txn_id: txnId, package_id: packageId });
              onClose?.();
            } else {
              // Payment overlay closed with completed=true but backend not yet fulfilled — keep polling briefly
              pollUntilPaid(txnId, 8).then((ok) => {
                if (ok) {
                  onPaid?.({ provider: "paddle", txn_id: txnId, package_id: packageId });
                  onClose?.();
                } else {
                  toast.info("Payment received. Fulfilment in progress — check My projects in a moment.");
                  onClose?.();
                }
              });
            }
          });
        }
      },
    })
      .then((p) => setPaddle(p))
      .catch((e) => {
        console.error("[paddle] init failed", e);
        toast.error("Card payment unavailable");
      })
      .finally(() => setIniting(false));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const confirmPaymentStatus = async (txnId) => {
    if (!txnId) return false;
    try {
      const r = await api.get(`/payments/status/${txnId}`);
      return r.data?.payment_status === "paid";
    } catch { return false; }
  };

  const pollUntilPaid = async (txnId, attempts = 6) => {
    for (let i = 0; i < attempts; i++) {
      await new Promise((res) => setTimeout(res, 1500));
      if (await confirmPaymentStatus(txnId)) return true;
    }
    return false;
  };

  const openPaddleOverlay = async () => {
    if (!paddle) { toast.error("Payment SDK still loading"); return; }
    setBusy(true);
    try {
      const r = await api.post("/payments/paddle/create-transaction", {
        package_id: packageId,
        project_id: projectId || null,
        origin_url: window.location.origin,
      });
      const txnId = r.data.transaction_id;
      currentTxnRef.current = txnId;
      track("checkout_initiated", { method: "paddle", package_id: packageId });
      paddle.Checkout.open({
        transactionId: txnId,
        settings: {
          displayMode: "overlay",
          theme: "light",
          successUrl: `${window.location.origin}/payment/success?provider=paddle&session_id=${txnId}`,
        },
      });
    } catch (e) {
      const detail = e?.response?.data?.detail || "";
      toast.error(detail.includes("price ID") ? "Paddle price IDs not set yet — ask admin" : "Could not open Paddle");
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" style={{ background: "rgba(20,26,23,0.55)", backdropFilter: "blur(6px)" }} data-testid="payment-modal">
      <div className="relative w-full max-w-lg rounded-lg" style={{ background: "var(--white)", border: "1px solid var(--hairline)" }}>
        <button onClick={onClose} className="absolute right-3 top-3 p-1 rounded hover:bg-black/5" data-testid="payment-modal-close">
          <X size={16} />
        </button>
        <div className="p-6 pb-3" style={{ borderBottom: "1px solid var(--hairline)" }}>
          <div className="overline mb-1">Checkout</div>
          <h2 className="font-display text-2xl">{packageName}</h2>
          <div className="mt-1 flex items-center gap-2 text-sm" style={{ color: "var(--muted)" }}>
            <span className="font-mono font-medium" style={{ color: "var(--charcoal)" }}>{amountLabel}</span>
            <span>· one-time payment</span>
          </div>
        </div>

        <div className="p-6">
          <div className="text-sm mb-3" style={{ color: "var(--body)" }}>Choose how you want to pay:</div>

          <label className={`flex items-start gap-3 p-4 rounded-md border cursor-pointer transition-colors ${method === "paddle" ? "border-[var(--brand-green)] bg-[rgba(26,111,43,0.04)]" : "border-[var(--hairline)] hover:border-[var(--brand-green)]"}`} data-testid="payment-option-paddle">
            <input type="radio" name="pay-method" checked={method === "paddle"} onChange={() => setMethod("paddle")} className="mt-1" />
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className="font-medium" style={{ color: "var(--charcoal)" }}>Card, Apple Pay, Google Pay or PayPal</span>
                <span className="pill" style={{ background: "var(--brand-green)", color: "white", fontSize: "0.6rem" }}>Recommended</span>
              </div>
              <div className="text-xs mt-1" style={{ color: "var(--muted)" }}>Secure checkout via Paddle · encrypted overlay stays on this page.</div>
            </div>
          </label>

          <label className={`mt-3 flex items-start gap-3 p-4 rounded-md border cursor-pointer transition-colors ${method === "paypal" ? "border-[var(--brand-green)] bg-[rgba(26,111,43,0.04)]" : "border-[var(--hairline)] hover:border-[var(--brand-green)]"}`} data-testid="payment-option-paypal">
            <input type="radio" name="pay-method" checked={method === "paypal"} onChange={() => setMethod("paypal")} className="mt-1" />
            <div className="flex-1">
              <div className="font-medium" style={{ color: "var(--charcoal)" }}>PayPal balance / linked account</div>
              <div className="text-xs mt-1" style={{ color: "var(--muted)" }}>Direct PayPal checkout — funded from your PayPal balance or connected sources.</div>
            </div>
          </label>

          <div className="mt-6">
            {method === "paddle" ? (
              <button
                onClick={openPaddleOverlay}
                disabled={busy || initing || !paddle}
                className="btn-primary w-full justify-center"
                data-testid="pay-with-paddle"
              >
                {busy || initing ? <Loader2 className="animate-spin" size={14} /> : <CreditCard size={14} />}
                {initing ? "Preparing…" : busy ? "Opening…" : `Pay ${amountLabel} securely`}
              </button>
            ) : (
              PAYPAL_CLIENT_ID ? (
                <PayPalScriptProvider options={{ clientId: PAYPAL_CLIENT_ID, currency: "USD", intent: "capture" }}>
                  <div data-testid="paypal-button-container">
                    <PayPalButtons
                      style={{ layout: "vertical", color: "gold", shape: "rect", label: "paypal" }}
                      forceReRender={[packageId, projectId]}
                      createOrder={async () => {
                        try {
                          const r = await api.post("/payments/paypal/create-order", {
                            package_id: packageId,
                            project_id: projectId || null,
                            origin_url: window.location.origin,
                          });
                          currentTxnRef.current = r.data.order_id;
                          track("checkout_initiated", { method: "paypal", package_id: packageId });
                          return r.data.order_id;
                        } catch (e) {
                          const detail = e?.response?.data?.detail || "";
                          toast.error(detail || "Could not start PayPal checkout");
                          throw e;
                        }
                      }}
                      onApprove={async (data) => {
                        try {
                          const r = await api.post(`/payments/paypal/capture/${data.orderID}`);
                          if (r.data.paid) {
                            track("report_purchase", { method: "paypal", package_id: packageId });
                            toast.success("Payment captured — unlocking report");
                            onPaid?.({ provider: "paypal", txn_id: data.orderID, package_id: packageId });
                            onClose?.();
                          } else {
                            toast.error("PayPal capture pending — try again in a moment");
                          }
                        } catch {
                          toast.error("Could not confirm PayPal capture");
                        }
                      }}
                      onCancel={() => toast.info("PayPal checkout cancelled")}
                      onError={(e) => { console.error("[paypal] error", e); toast.error("PayPal error — try another method"); }}
                    />
                  </div>
                </PayPalScriptProvider>
              ) : (
                <div className="text-sm p-3 rounded-md" style={{ background: "var(--paper)", color: "var(--muted)" }}>
                  PayPal is not configured on this deployment.
                </div>
              )
            )}
          </div>

          <div className="mt-5 flex items-center gap-2 text-xs" style={{ color: "var(--muted)" }}>
            <ShieldCheck size={12} style={{ color: "var(--brand-green)" }} />
            Payment is verified server-side before any report is unlocked.
          </div>
        </div>
      </div>
    </div>
  );
};
